import {
  Directive,
  ElementRef,
  EventEmitter, Host,
  HostBinding,
  HostListener,
  Injector,
  Input, NgZone, OnDestroy, OnInit, AfterViewInit,
  Optional,
  Output,
} from '@angular/core';
import {AbstractControl, FormArray, FormGroup, NgControl, NgModel, ValidationErrors} from "@angular/forms";
import {
  NbDynamicOverlay,
  NbDynamicOverlayHandler, NbLayoutDirectionService, NbLayoutScrollService,
  NbOverlayContainer,
  NbOverlayService,
  NbPositionBuilderService, NbToastrService,
  NbTooltipComponent,
  NbTooltipDirective,
  NbTrigger,
  NbTriggerStrategyBuilderService
} from "@nebular/theme";
import {TranslateService} from "@ngx-translate/core";

@Directive({
    selector: '[ngModel], [formControl], [formControlName]',
    standalone: false
})
export class NativeElementInjectorDirective {
  constructor(private el: ElementRef, private control : NgControl, @Optional() private model : NgModel) {
    if (!! model)
      (<any>model.control).nativeElement = el.nativeElement;
    else
      (<any>control).nativeElement = el.nativeElement;
  }
}

@Directive({
    selector: 'nb-select, ngx-intl-tel-input, bst-list-adder, bst-places-editor, bst-scale-editor, bst-password, [bstForwardFocus]',
    standalone: false
})
export class ForwardFocusDirective implements OnInit{

  @Input() bstForwardFocus: string;

  @HostListener('focus', ['$event']) focus($event){
    const sel: string = this.bstForwardFocus ? this.bstForwardFocus
      : 'input, select, textarea, button, [tabindex]:not([tabindex="-1"])';
    const el: HTMLElement = this.host.nativeElement.querySelector(sel);
    if(el && document.activeElement !== el){
      el.focus();
    }
  }

  constructor(private host: ElementRef) {}

  ngOnInit() {
    if(!this.host.nativeElement.hasAttribute('tabindex')) {
      this.host.nativeElement.setAttribute('tabindex', '0');
    }
  }
}

@Directive({
    selector: '[bstErroring]',
    providers: [NbDynamicOverlayHandler, NbDynamicOverlay],
    standalone: false
})
export class ErroringDirective implements AfterViewInit, OnInit, OnDestroy{

  private tools: {[key: string]: NbTooltipDirective} = {};

  @HostListener('click', ['$event']) async onClick($event: MouseEvent){
    let first = true;
    const regrouping = async (controls: { [key: string]: AbstractControl; } | AbstractControl[]) => {
      for(let c in controls){
        const control: AbstractControl = controls[c];
        if(control instanceof FormGroup || control instanceof FormArray){
          await regrouping((<FormGroup|FormArray>control).controls);
        }
        else {
          try {
            const el = (<any>control).nativeElement ?? document.body.querySelector('[formcontrolname="'+c+'"],[ng-reflect-name="'+c+'"],[data-name="'+c+'"]');

            if (el) {
              if (control.errors) {
                console.log(c, control.errors);
                await this.addToolTip(c, el, control.errors);
                if (first) {
                  el.focus();
                  first = false;
                }
              } else {
                await this.removeTooltip(c, el);
              }
            }
            else if(control.errors){
              console.log('Unknown element for error with control: '+c, control);
            }
          } catch (e){
            if(!(e instanceof SyntaxError)){
              console.error(e);
            }
          }
        }
      }
    };

    await regrouping(this.bstErroring.controls);

    if(this.bstErroring && this.bstErroring.invalid){
      $event.stopPropagation();
      $event.preventDefault();
      if(first && this.bstErroring.errors){
        const estrings = await this.translate.get(Object.keys(this.bstErroring.errors).map(o => 'errors.' + o)).toPromise();
        this.toast.danger(Object.values(estrings).join(', '), this.translate.instant('error'), {
          icon: 'alert-circle'
        });
      }
    }
    else if(this.bstErroringSuccess){
      $event.stopPropagation();
      $event.preventDefault();
      this.bstErroringSuccess.emit(this.bstErroring);
    }
  }

  @Input() bstErroring: FormGroup = null;
  @Output() bstErroringSuccess: EventEmitter<any>= new EventEmitter<any>();

  @HostBinding('class.invalid') get invalid() {
    return !this.bstErroring || this.bstErroring.invalid;
  }

  constructor(private host: ElementRef,
              private toast: NbToastrService,
              private injector: Injector,
              private pos: NbPositionBuilderService,
              private tri: NbTriggerStrategyBuilderService,
              private ovrly: NbOverlayService,
              private zone: NgZone,
              private scroll: NbLayoutScrollService,
              private translate: TranslateService,
              private directionService: NbLayoutDirectionService) {
  }

  private cyclethrough(dom: HTMLElement, tag:string, children:string, add:boolean = true){
    if(dom.tagName.toLocaleLowerCase() === tag){
      const col: HTMLCollectionOf<Element> = dom.getElementsByTagName(children);
      for(let i=0; i< col.length; i++){
        if(add) {
          col.item(i).classList.add('status-danger');
        }
        else{
          col.item(i).classList.remove('status-danger');
        }
      }
    }
  }

  private async addToolTip(name: string, dom: HTMLElement, errors: ValidationErrors){
    dom.classList.add('status-danger');
    this.cyclethrough(dom, 'nb-radio-group', 'nb-radio', true);

    const cur = this.tools[name];

    // Make errors
    const estrings = await this.translate.get(Object.keys(errors).map(o => 'errors.' + o)).toPromise();
    const estring = estrings ? Object.values(estrings).join(', ') : 'Invalid';

    // Make tooltip
    if(!cur) {
      const el: ElementRef = new ElementRef<any>(dom);
      const container: NbOverlayContainer = this.injector.get<NbOverlayContainer>(NbOverlayContainer);
      const over: NbDynamicOverlay = new NbDynamicOverlay(this.ovrly, this.zone, container);
      const handler = new NbDynamicOverlayHandler(this.pos, this.tri, over, this.directionService);
      const dir = new NbTooltipDirective(el, handler);

      dir.status = 'danger';
      dir.trigger = NbTrigger.FOCUS;

      dir.content = estring;
      dir.ngOnInit();
      dir.ngAfterViewInit();
      dir.show();
      this.tools[name] = dir;
    }
    else{
      cur.content = estring;
      cur.rebuild();
      cur.show();
    }

  }

  private async removeTooltip(name: string, dom: HTMLElement){
    dom.classList.remove('status-danger');
    this.cyclethrough(dom, 'nb-radio-group', 'nb-radio', false);

    if(this.tools[name]){
      this.tools[name].ngOnDestroy();
      delete this.tools[name];
    }
  }

  ngOnInit() {

  }

  ngAfterViewInit() {

  }

  ngOnDestroy() {
    if(this.tools){
      for(let i in this.tools){
        this.tools[i].ngOnDestroy();
        delete this.tools[i];
      }
    }
  }


}
